import {messages} from '@smartmixin/i18n';
import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ProviderStationLink, StationSource} from '../api/types';
import {Dates, Station} from '../utils';

type RetirableStation = {
  source?: StationSource;
  timezone?: string;
  link?: ProviderStationLink | null;
};

type StationRetirement = {
  /** Epoch seconds the station left its provider account listing. */
  since: number;
  /** `since` as a medium date in the station's time zone, e.g. "Sep 19, 2026". */
  date: string;
  /** "No longer reported by {provider} since {date}", for station cards. */
  label: string;
  /** "No longer reported", for compact rows and pickers. */
  shortLabel: string;
  /** Why an alert on this station can never trigger. */
  alertNotice: string;
};

/**
 * The strings for a station that left its provider account listing (#324),
 * or null while it is live. Backend !684 keeps such a station, with its
 * history, instead of deleting it — usually the old device after a hardware
 * swap — but it receives no new data. The date is in the station's time zone.
 */
const useStationRetirement = (
  station: RetirableStation | null | undefined,
): StationRetirement | null => {
  const {formatMessage} = useIntl();
  const since = station ? Station.getMissingFromListingSince(station) : null;
  const provider =
    station?.source !== undefined
      ? Station.getSourceName(station.source)
      : null;
  const timezone = station?.timezone;

  return useMemo(() => {
    if (since === null) {
      return null;
    }
    const date = Dates.formatDate(since, timezone ?? 'UTC', 'll');
    return {
      since,
      date,
      label: formatMessage(messages.stations.noLongerReportedBy, {
        provider: provider ?? formatMessage(messages.common.unknown),
        date,
      }),
      shortLabel: formatMessage(messages.stations.noLongerReported),
      alertNotice: formatMessage(messages.alerts.stationNoLongerReports),
    };
  }, [formatMessage, since, provider, timezone]);
};

export default useStationRetirement;
